import { useEffect } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { Trans, useLingui } from '@lingui/react/macro';
import { useFormContext } from 'react-hook-form';

import { amountToIndex, indexToAmount, LAST_INDEX } from './amountScale';
import styles from './AmountScreen.module.css';
import { MAX_AMOUNT, MIN_AMOUNT, sanitiseAmount } from './sanitiseAmount';
import { useNumberFormat } from '../../../../hooks/useNumberFormat';
import { useSignupParams } from '../../../../hooks/useSignupParams';
import { getAmplitudeDefaultEventProperties } from '../../../../utils/amplitudeEvents';
import { TMobileSignupForm } from '../../form/form';
import { useStageFieldTracking } from '../../form/MobileSignupFieldTracking';
import { useMobileSignupNavContext } from '../../hooks/useMobileSignupNavContext';
import { RangeSlider } from '../../RangeSlider/RangeSlider';
import { StepLayout } from '../../StepLayout/StepLayout';

const DEFAULT_AMOUNT = 25000;

// DIT-669. Reads/writes the shared mobile signup form store (DIT-664). The
// store keeps `amount` as a string (as the persistence layer expects); the
// slider works in numbers, so we convert only at that boundary.
export const AmountScreen = () => {
  const { t } = useLingui();
  const { formatCurrency } = useNumberFormat();
  const { watch, setValue } = useFormContext<TMobileSignupForm>();
  const { goNext } = useMobileSignupNavContext();
  const { referredAmount } = useSignupParams();
  const { fieldProps } = useStageFieldTracking();

  const amount = watch('amount');
  const sliderValue = Number(amount) || DEFAULT_AMOUNT;

  useEffect(() => {
    tracking.ampli.queryParamLoaded({
      query_param: 'amount',
      query_value: referredAmount ?? undefined,
      query_exists: Boolean(referredAmount),
      ...getAmplitudeDefaultEventProperties(),
    });
  }, [referredAmount]);

  // Seed the default so the value shown on the slider is also what gets
  // persisted. Non-dirty, so the provider's async reset from a saved
  // application still wins if the user already has an amount.
  useEffect(() => {
    if (!amount) {
      const seed = sanitiseAmount(referredAmount) ?? DEFAULT_AMOUNT;
      setValue('amount', String(seed), { shouldDirty: false });
    }
  }, [amount, referredAmount, setValue]);

  const isValid = sliderValue >= MIN_AMOUNT && sliderValue <= MAX_AMOUNT;

  return (
    <StepLayout
      stageTitle={t`Credit request`}
      heading={t`How much would you like?`}
      ctaDisabled={!isValid}
      onContinue={goNext}
      fieldProps={fieldProps}
    >
      <p className={styles.amount}>{formatCurrency(sliderValue)}</p>
      <p className={styles.note}>
        <Trans>non-binding · doesn't affect your credit score</Trans>
      </p>
      <RangeSlider
        name="amount"
        min={0}
        max={LAST_INDEX}
        step={1}
        value={amountToIndex(sliderValue)}
        onChange={(index) =>
          setValue('amount', String(indexToAmount(index)), {
            shouldDirty: true,
          })
        }
        ariaLabel={t`How much would you like?`}
        valueText={formatCurrency(sliderValue)}
        minLabel={formatCurrency(MIN_AMOUNT)}
        maxLabel={formatCurrency(MAX_AMOUNT)}
      />
    </StepLayout>
  );
};
